import { Income } from "../../lib/types";
import { incomeAPI } from "../api/api";

// Database operations for Income - API client implementation
// Define the interface for the async methods
interface AsyncIncomeDB {
  getAllIncome: () => Promise<Income[]>;
  getIncomeById: (id: string) => Promise<Income | null>;
  addIncome: (income: Omit<Income, "id">) => Promise<Income>;
  updateIncome: (income: Income) => Promise<Income>;
  deleteIncome: (id: string) => Promise<void>;
  togglePaid: (id: string) => Promise<Income>;
}

// Implementation of the async methods
const asyncImplementation: AsyncIncomeDB = {
  getAllIncome: async (): Promise<Income[]> => {
    try {
      return await incomeAPI.getAll();
    } catch (error) {
      console.error("Error getting all income:", error);
      return [];
    }
  },

  getIncomeById: async (id: string): Promise<Income | null> => {
    try {
      return await incomeAPI.getById(id);
    } catch (error) {
      console.error(`Error getting income with id ${id}:`, error);
      return null;
    }
  },

  addIncome: async (income: Omit<Income, "id">): Promise<Income> => {
    try {
      return await incomeAPI.add(income);
    } catch (error) {
      console.error("Error adding income:", error);
      throw error;
    }
  },

  updateIncome: async (income: Income): Promise<Income> => {
    try {
      return await incomeAPI.update(income);
    } catch (error) {
      console.error("Error updating income:", error);
      throw error;
    }
  },

  deleteIncome: async (id: string): Promise<void> => {
    try {
      await incomeAPI.delete(id);
    } catch (error) {
      console.error("Error deleting income:", error);
      throw error;
    }
  },

  togglePaid: async (id: string): Promise<Income> => {
    try {
      const result = await incomeAPI.togglePaid(id);
      if (!result) {
        throw new Error(`Income with id ${id} could not be toggled.`);
      }
      return result;
    } catch (error) {
      console.error("Error toggling paid status for income:", error);
      throw error;
    }
  },
};

// Define a separate export - this makes TypeScript happy by allowing
// the module to be used with only the async methods
const incomeDB: AsyncIncomeDB = asyncImplementation;

export default incomeDB;
